import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import { create } from 'zustand';
import { useBrandContextStore } from '@/lib/branding/brandContextStore';
import { persist } from 'zustand/middleware';
import type { Role, RoleRef, User } from '@/lib/api/types';

// Single source of truth for Sentry identity — applied on login, logout, and
// after store rehydration on app boot. Keeps the "AnonymousUser" problem
// from coming back. Don't sync `Sentry.setUser` from individual components.
function syncSentryUser(user: User | null, companyId: number | null): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username:
      user.username ??
      (`${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || undefined),
    companyId: companyId ?? undefined,
  });
}

/**
 * Backend returns `user.role` as either a string (legacy / login path) or an
 * object `{id, name}` (joined `/profile`). The store keeps a flat string so
 * callers (`OnboardingGate`, route guards, etc.) can compare with `===`.
 */
function normalizeRole(r?: RoleRef | null): Role | null {
  if (!r) return null;
  return (typeof r === 'string' ? r : r.name) as Role;
}

export type AppType = 'onxite' | 'xamplin' | 'console' | 'xotski' | 'xurvey' | 'did' | string;

/** Entity-key → list of allowed actions (kebab-case keys, e.g. "xp-project"). */
export type PermissionsMap = Record<string, string[]>;

interface JwtPayload {
  exp?: number;
  sub?: number | string;
  app?: AppType;
  permissions?: PermissionsMap;
}

function parseExpiresAt(token: string | null): number | null {
  if (!token) return null;
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function parsePermissions(token: string | null): PermissionsMap {
  if (!token) return {};
  try {
    return jwtDecode<JwtPayload>(token).permissions ?? {};
  } catch {
    return {};
  }
}

function parseApp(token: string | null): AppType | null {
  if (!token) return null;
  try {
    return jwtDecode<JwtPayload>(token).app ?? null;
  } catch {
    return null;
  }
}

interface SessionPayload {
  token: string;
  refreshToken?: string | null;
  user: User;
  role?: Role;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: User | null;
  role: Role | null;
  permissions: PermissionsMap;
  app: AppType | null;
  /** Email kept between OTP send and verify (not persisted). */
  pendingEmail: string | null;

  setSession: (payload: SessionPayload) => void;
  patchUser: (patch: Partial<User>) => void;
  setPendingEmail: (email: string | null) => void;
  logout: () => void;
  isExpired: () => boolean;
  willExpireSoon: (thresholdMs?: number) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      role: null,
      permissions: {},
      app: null,
      pendingEmail: null,

      setSession: ({ token, refreshToken, user, role }) => {
        syncSentryUser(user, user.companyId ?? null);
        set({
          token,
          refreshToken: refreshToken ?? null,
          expiresAt: parseExpiresAt(token),
          user,
          role: normalizeRole(role ?? user.role),
          permissions: parsePermissions(token),
          app: parseApp(token),
        });
      },

      patchUser: (patch) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...patch } : state.user,
        })),

      setPendingEmail: (email) => set({ pendingEmail: email }),

      logout: () => {
        syncSentryUser(null, null);
        // La marca es del usuario que se va, no del navegador (095).
        useBrandContextStore.getState().clear();
        set({
          token: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          role: null,
          permissions: {},
          app: null,
          pendingEmail: null,
        });
      },

      isExpired: () => {
        const exp = get().expiresAt;
        return exp !== null && Date.now() >= exp;
      },

      willExpireSoon: (thresholdMs = 60_000) => {
        const exp = get().expiresAt;
        return exp !== null && Date.now() + thresholdMs >= exp;
      },
    }),
    {
      name: 'xotski.auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        user: state.user,
        role: state.role,
        permissions: state.permissions,
        app: state.app,
      }),
      // After page reload, the persisted user is restored without going through
      // setSession — Sentry would still see "AnonymousUser". Sync identity here.
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          syncSentryUser(state.user, state.user.companyId ?? null);
        }
      },
    },
  ),
);
